import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'

import { REVENUE_TABS, REVENUE_TAB_LABELS, type RevenueTab } from './params'

export const tabId = (tab: RevenueTab) => `rv-tab-${tab}`
export const panelId = (tab: RevenueTab) => `rv-panel-${tab}`

/**
 * The page's tabs: an ARIA tablist (they switch a panel of one page; `?tab=` keeps the
 * choice in the URL), as the competition page's are. A roving tabindex puts only the open
 * tab in the Tab order; the arrow keys, Home and End move focus, and Enter or Space opens
 * the focused tab — manually, since each tab reads its own routes when it opens.
 */
export function RevenueTabs({ active, onSelect }: { active: RevenueTab; onSelect: (tab: RevenueTab) => void }) {
  const refs = useRef(new Map<RevenueTab, HTMLButtonElement>())

  // On a phone the list scrolls sideways inside itself: bring the open tab into it, never scrolling the page.
  useEffect(() => {
    const tab = refs.current.get(active)
    const list = tab?.parentElement
    if (!tab || !list) return
    const start = tab.offsetLeft - list.offsetLeft
    if (start < list.scrollLeft) list.scrollLeft = start
    else if (start + tab.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollLeft = start + tab.offsetWidth - list.clientWidth
  }, [active])

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = REVENUE_TABS.length - 1
    const moves: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }
    const target = moves[event.key]
    if (target === undefined) return
    event.preventDefault()
    const tab = REVENUE_TABS[target]
    if (tab) refs.current.get(tab)?.focus()
  }

  return (
    <div className="rv-tabs" role="tablist" aria-label="Revenue">
      {REVENUE_TABS.map((tab, index) => {
        const selected = tab === active
        return (
          <button
            key={tab}
            ref={(element) => {
              if (element) refs.current.set(tab, element)
              else refs.current.delete(tab)
            }}
            type="button"
            role="tab"
            id={tabId(tab)}
            aria-controls={selected ? panelId(tab) : undefined}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className="rv-tabs__tab"
            onClick={() => onSelect(tab)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {REVENUE_TAB_LABELS[tab]}
          </button>
        )
      })}
    </div>
  )
}

export function RevenueTabPanel({ tab, children }: { tab: RevenueTab; children: ReactNode }) {
  return (
    <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)} tabIndex={0} className="rv-tabpanel">
      {children}
    </div>
  )
}
